"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { ApiWave } from "@/generated/models/ApiWave";
import { WsDropUpdateMessage, WsMessageType } from "@/helpers/Types";
import { useWebSocketMessage } from "@/services/websocket/useWebSocketMessage";
import { useCallback, useContext, useEffect, useState } from "react";

/**
 * Interface for tracking new drops count for a wave
 */
export interface MinimalWaveNewDropsCount {
  readonly count: number;
  readonly latestDropTimestamp: number | null;
  readonly firstUnreadSerialNo: number | null;
}
export function getNewestTimestamp(cached: number | null | undefined = null, server: number | null | undefined = null): number | null {
  if (cached == null && server == null) {
    return null;
  }
  if (cached == null) {
    return server;
  }
  if (server == null) {
    return cached;
  }
  return Math.max(cached, server);
}

/**
 * Hook to manage new drop counts via WebSockets
 *
 * @param activeWaveId - The ID of the currently active wave
 * @param waves - List of waves for which to track new drops
 * @param refetchWaves - Function to refetch waves data when needed
 * @returns Object containing newDropsCounts and reset function
 */
function useNewDropCounter(activeWaveId, waves, refetchWaves) {
  const $ = _c(22);
  const {
    connectedProfile
  } = useContext(AuthContext);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [newDropsCounts, setNewDropsCounts] = useState(t0);
  let t1;
  if ($[1] !== waves) {
    t1 = waveId => {
      setNewDropsCounts(prev => ({
        ...prev,
        [waveId]: {
          count: 0,
          latestDropTimestamp: getNewestTimestamp(prev[waveId]?.latestDropTimestamp, waves.find(wave => wave.id === waveId)?.metrics.latest_drop_timestamp ?? null),
          firstUnreadSerialNo: null
        }
      }));
    };
    $[1] = waves;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const resetWaveNewDropsCount = t1;
  let t2;
  if ($[3] !== waves) {
    t2 = () => {
      setNewDropsCounts(prev_0 => {
        const newCounts = {};
        waves.forEach(wave_0 => {
          newCounts[wave_0.id] = {
            count: 0,
            latestDropTimestamp: getNewestTimestamp(prev_0[wave_0.id]?.latestDropTimestamp, wave_0.metrics.latest_drop_timestamp ?? null),
            firstUnreadSerialNo: null
          };
        });
        return newCounts;
      });
    };
    $[3] = waves;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const resetAllWavesNewDropsCount = t2;
  let t3;
  let t4;
  if ($[5] !== activeWaveId || $[6] !== resetWaveNewDropsCount) {
    t3 = () => {
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible" && activeWaveId) {
          resetWaveNewDropsCount(activeWaveId);
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    };
    t4 = [activeWaveId, resetWaveNewDropsCount];
    $[5] = activeWaveId;
    $[6] = resetWaveNewDropsCount;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[9] !== activeWaveId || $[10] !== resetWaveNewDropsCount) {
    t5 = () => {
      if (activeWaveId) {
        resetWaveNewDropsCount(activeWaveId);
      }
    };
    t6 = [activeWaveId, resetWaveNewDropsCount];
    $[9] = activeWaveId;
    $[10] = resetWaveNewDropsCount;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== activeWaveId || $[14] !== connectedProfile?.handle || $[15] !== refetchWaves || $[16] !== waves) {
    t7 = message => {
      if (!message?.wave.id) {
        return;
      }
      const waveId_0 = message.wave.id;
      const wave_1 = waves.find(w => w.id === waveId_0);
      if (!wave_1) {
        refetchWaves();
        return;
      }
      if (wave_1.metrics.muted) {
        return;
      }
      if (connectedProfile?.handle?.toLowerCase() === message.author.handle?.toLowerCase()) {
        return setNewDropsCounts(prev_1 => {
          const currentCount = prev_1[waveId_0]?.count ?? 0;
          const currentLatestDropTimestamp = prev_1[waveId_0]?.latestDropTimestamp ?? null;
          return {
            ...prev_1,
            [waveId_0]: {
              count: currentCount,
              latestDropTimestamp: Math.max(message.created_at, currentLatestDropTimestamp ?? 0),
              firstUnreadSerialNo: prev_1[waveId_0]?.firstUnreadSerialNo ?? null
            }
          };
        });
      }
      if (waveId_0 === activeWaveId && document.visibilityState === "visible") {
        return setNewDropsCounts(prev_2 => {
          const currentCount_0 = prev_2[waveId_0]?.count ?? 0;
          const currentLatestDropTimestamp_0 = prev_2[waveId_0]?.latestDropTimestamp ?? null;
          return {
            ...prev_2,
            [waveId_0]: {
              count: currentCount_0,
              latestDropTimestamp: Math.max(message.created_at, currentLatestDropTimestamp_0 ?? 0),
              firstUnreadSerialNo: prev_2[waveId_0]?.firstUnreadSerialNo ?? null
            }
          };
        });
      }
      setNewDropsCounts(prev_3 => {
        const currentCount_1 = prev_3[waveId_0]?.count ?? 0;
        const currentLatestDropTimestamp_1 = prev_3[waveId_0]?.latestDropTimestamp ?? null;
        const currentFirstUnread = prev_3[waveId_0]?.firstUnreadSerialNo ?? null;
        return {
          ...prev_3,
          [waveId_0]: {
            count: currentCount_1 + 1,
            latestDropTimestamp: Math.max(message.created_at, currentLatestDropTimestamp_1 ?? 0),
            firstUnreadSerialNo: currentFirstUnread === null ? message.serial_no : Math.min(currentFirstUnread, message.serial_no)
          }
        };
      });
    };
    $[13] = activeWaveId;
    $[14] = connectedProfile?.handle;
    $[15] = refetchWaves;
    $[16] = waves;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useWebSocketMessage(WsMessageType.DROP_UPDATE, t7);
  let t8;
  if ($[18] !== newDropsCounts || $[19] !== resetAllWavesNewDropsCount || $[20] !== resetWaveNewDropsCount) {
    t8 = {
      newDropsCounts,
      resetWaveNewDropsCount,
      resetAllWavesNewDropsCount
    };
    $[18] = newDropsCounts;
    $[19] = resetAllWavesNewDropsCount;
    $[20] = resetWaveNewDropsCount;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
}
export default useNewDropCounter;